const DEFAULT_PIP_SIZE = 0.0001
const JPY_XAU_PIP_SIZE = 0.01
const PRICE_FLOOR = 1e-6

export interface SymbolMetadata {
  pipSize: number | string
  contractSize: number
  conversionSymbol?: string
  conversionType?: 'MULTIPLY' | 'DIVIDE' | ''
}

export interface UnityUnrealizedPnlInput {
  symbol: string
  side: 'BUY' | 'SELL'
  entryPrice: number
  lots: number
  currentPrice?: number
  latestPricesBySymbol: Record<string, number>
  metadata?: SymbolMetadata
}

function normalizeSymbolKey(symbol: string): string {
  return symbol.toUpperCase().replace(/[^A-Z]/g, '')
}

function canonicalSymbol(symbol: string): string {
  const normalized = normalizeSymbolKey(symbol)
  if (normalized.length <= 6) {
    return normalized
  }
  return normalized.slice(-6)
}

function extractBaseQuote(symbol: string): { baseCcy: string; quoteCcy: string } | null {
  const canonical = canonicalSymbol(symbol)
  if (canonical.length < 6) {
    return null
  }

  return {
    baseCcy: canonical.slice(0, 3),
    quoteCcy: canonical.slice(3, 6),
  }
}

function getPipSize(symbol: string, metadata?: SymbolMetadata): number {
  // Metadata-first: use provided pip size from backend
  if (metadata && metadata.pipSize) {
    const pipSize = typeof metadata.pipSize === 'string' ? Number(metadata.pipSize) : metadata.pipSize
    if (Number.isFinite(pipSize) && pipSize > 0) {
      return pipSize
    }
  }

  // Fallback to heuristic for missing metadata
  const normalized = symbol.toUpperCase()
  return normalized.includes('JPY') || normalized.includes('XAU') ? JPY_XAU_PIP_SIZE : DEFAULT_PIP_SIZE
}

function getContractSize(symbol: string, metadata?: SymbolMetadata): number {
  // Metadata-first: use provided contract size from backend
  if (metadata && metadata.contractSize && Number.isFinite(metadata.contractSize) && metadata.contractSize > 0) {
    return metadata.contractSize
  }

  // Fallback to standard contract size
  return 100000
}

function toPositiveFinite(value: number | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null
  }
  return value
}

function getLiveRate(symbol: string, latestPricesBySymbol: Record<string, number>): number | null {
  const canonicalTarget = canonicalSymbol(symbol)
  const directCandidates = [symbol, symbol.replace('/', ''), canonicalTarget]
  if (!symbol.startsWith('F:')) {
    directCandidates.push(`F:${symbol}`)
  }

  for (const candidate of directCandidates) {
    const rate = toPositiveFinite(latestPricesBySymbol[candidate])
    if (rate !== null) {
      return rate
    }
  }

  const normalizedTarget = normalizeSymbolKey(symbol)
  if (!normalizedTarget) {
    return null
  }

  for (const [priceSymbol, rawRate] of Object.entries(latestPricesBySymbol)) {
    const rate = toPositiveFinite(rawRate)
    if (rate === null) {
      continue
    }

    const normalizedSymbol = normalizeSymbolKey(priceSymbol)
    const canonicalPriceSymbol = canonicalSymbol(priceSymbol)
    if (
      canonicalPriceSymbol === canonicalTarget ||
      normalizedSymbol === normalizedTarget ||
      (normalizedSymbol.length >= 6 && normalizedSymbol.slice(-6) === normalizedTarget)
    ) {
      return rate
    }
  }

  return null
}

function tryGetQuoteToUsdRate(
  quoteCcy: string,
  latestPricesBySymbol: Record<string, number>
): number | null {
  if (quoteCcy === 'USD') {
    return 1
  }

  const directPairRate = getLiveRate(`${quoteCcy}/USD`, latestPricesBySymbol)
  if (directPairRate !== null) {
    return directPairRate
  }

  const inversePairRate = getLiveRate(`USD/${quoteCcy}`, latestPricesBySymbol)
  if (inversePairRate !== null) {
    return 1 / inversePairRate
  }

  return null
}

function computePipValueUsd(
  symbol: string,
  atPrice: number,
  pipSize: number,
  contractSize: number,
  latestPricesBySymbol: Record<string, number>,
  metadata?: SymbolMetadata
): number | null {
  // Metadata-first: use backend-defined conversion behavior
  if (metadata && metadata.conversionSymbol && metadata.conversionType) {
    const conversionRate = getLiveRate(metadata.conversionSymbol, latestPricesBySymbol)
    if (conversionRate === null) {
      // Missing conversion price: return null to fall back to backend PnL
      return null
    }

    if (metadata.conversionType === 'MULTIPLY') {
      return contractSize * pipSize * conversionRate
    } else if (metadata.conversionType === 'DIVIDE') {
      return (contractSize * pipSize) / Math.max(conversionRate, PRICE_FLOOR)
    }
  }

  // Fallback to heuristic for missing metadata
  const currencies = extractBaseQuote(symbol)
  if (!currencies) {
    return contractSize * pipSize
  }

  const { baseCcy, quoteCcy } = currencies
  if (quoteCcy === 'USD') {
    return contractSize * pipSize
  }

  if (baseCcy === 'USD') {
    return (contractSize * pipSize) / Math.max(atPrice, PRICE_FLOOR)
  }

  const quoteToUsd = tryGetQuoteToUsdRate(quoteCcy, latestPricesBySymbol)
  if (quoteToUsd === null) {
    return null
  }

  return contractSize * pipSize * quoteToUsd
}

export function computeUnityUnrealizedPnlUsd(input: UnityUnrealizedPnlInput): number | null {
  const { symbol, side, entryPrice, lots, currentPrice, latestPricesBySymbol, metadata } = input

  const resolvedCurrentPrice = toPositiveFinite(currentPrice) ?? getLiveRate(symbol, latestPricesBySymbol)

  if (resolvedCurrentPrice === null || !Number.isFinite(entryPrice)) {
    return null
  }
  if (entryPrice <= 0) {
    return 0
  }

  const normalizedLots = Math.max(1, Number.isFinite(lots) ? lots : 0)
  const pipSize = getPipSize(symbol, metadata)
  const contractSize = getContractSize(symbol, metadata)
  const diff =
    side.toUpperCase() === 'SELL'
      ? entryPrice - resolvedCurrentPrice
      : resolvedCurrentPrice - entryPrice
  const pips = diff / pipSize

  const pipValueUsd = computePipValueUsd(
    symbol,
    resolvedCurrentPrice,
    pipSize,
    contractSize,
    latestPricesBySymbol,
    metadata
  )
  if (pipValueUsd === null) {
    return null
  }

  return pips * pipValueUsd * normalizedLots
}
